import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import QRCode from "react-qr-code";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { sendQRCodeEmail } from "@/services/EmailService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [name, setName] = useState("");
  const [visitorType, setVisitorType] = useState("visita");
  const [area, setArea] = useState("");
  const [visitReason, setVisitReason] = useState("");
  const [numberOfVisitors, setNumberOfVisitors] = useState(1);
  const [notes, setNotes] = useState("");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [expirationTime, setExpirationTime] = useState("");
  const [isMultiScan, setIsMultiScan] = useState(false);
  const [vehiclePlate, setVehiclePlate] = useState("");
  const [vehicleBrand, setVehicleBrand] = useState("");
  const [vehicleColor, setVehicleColor] = useState("");
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedVisitorNotes, setSelectedVisitorNotes] = useState(null);
  const [availableAreas, setAvailableAreas] = useState([]);
  const [availableReasons, setAvailableReasons] = useState([]);
  const [activeView, setActiveView] = useState('form'); // 'form', 'notes', or 'bulk'
  const [notesFilter, setNotesFilter] = useState('all');
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkExpDate, setBulkExpDate] = useState('');
  const [bulkExpTime, setBulkExpTime] = useState('');
  const [bulkResults, setBulkResults] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [statistics, setStatistics] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const { user, logout, token } = useAuth();
  
  // Check if user is from Bodas department
  const isBodasUser = user?.department?.toLowerCase().includes('boda') || 
                      user?.department === 'Oficina de Bodas' ||
                      user?.department === 'Villa del Palmar Bodas' ||
                      user?.department === 'Villa del Arco Bodas' ||
                      user?.department === 'Villa La Estancia Bodas';

  // Check if user is admin
  const isAdmin = user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'admin_master';
  const isAdminMaster = user?.role?.toLowerCase() === 'admin_master';

  // Filter visitors based on notes filter
  const filteredVisitors = visitors.filter(v => {
    if (notesFilter === 'all') return true;
    if (notesFilter === 'with-notes') return v.notes && v.notes.trim() !== '';
    if (notesFilter === 'pending') return v.status === 'pending';
    if (notesFilter === 'used') return v.status === 'used';
    return true;
  });

  // Fetch available areas from backend
  useEffect(() => {
    const fetchAreas = async () => {
      try {
        const response = await axios.get(`${API}/department-areas/user/my-areas`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const areas = response.data.areas || [];
        setAvailableAreas(areas);
        if (areas.length > 0 && !area) {
          setArea(areas[0]);
        }
      } catch (error) {
        // Fallback to user's department if API fails
        setAvailableAreas([user?.department || 'ventas']);
      }
    };
    
    if (token) {
      fetchAreas();
    }
  }, [token, user?.department]);

  // Fetch available visit reasons from backend
  useEffect(() => {
    const fetchReasons = async () => {
      try {
        const response = await axios.get(`${API}/visit-reasons`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const reasons = response.data.reasons || [];
        setAvailableReasons(reasons);
      } catch (error) {
        // Fallback to default reasons
        setAvailableReasons(['Visita', 'Cita', 'Entrevista', 'Cena', 'Desayuno', 'Reunión', 'Entrega', 'Inspección']);
      }
    };
    
    if (token) {
      fetchReasons();
    }
  }, [token]);

  // Set default area when availableAreas changes
  useEffect(() => {
    if (availableAreas.length > 0 && !area) {
      setArea(availableAreas[0]);
    }
  }, [availableAreas, area]);

  // Fetch properties
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await axios.get(`${API}/properties`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProperties(response.data || []);
        // Set first property as default if available
        if (response.data?.length > 0 && !selectedProperty) {
          // Admin master starts with "all properties" view, others get first property
          if (user?.role !== 'admin_master') {
            setSelectedProperty(response.data[0].id);
          }
          // For admin_master, selectedProperty stays '' which means "all"
        }
      } catch (error) {
        // Silently ignore property fetch errors
      }
    };
    
    if (token) {
      fetchProperties();
    }
  }, [token]);

  const fetchStatistics = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const url = selectedProperty 
        ? `${API}/statistics?property_id=${selectedProperty}`
        : `${API}/statistics`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatistics(response.data);
    } catch (error) {
      // Statistics fetch silently fails
    }
  }, [token, selectedProperty, isAdmin]);

  const fetchVisitors = useCallback(async () => {
    try {
      const url = selectedProperty 
        ? `${API}/visitors?property_id=${selectedProperty}`
        : `${API}/visitors`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVisitors(response.data);
    } catch (error) {
      // Visitors fetch silently fails
    }
  }, [token, selectedProperty]);

  useEffect(() => {
    fetchVisitors();
    fetchStatistics();
    const interval = setInterval(() => {
      fetchVisitors();
      fetchStatistics();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchVisitors, fetchStatistics]);

  const handleBulkFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBulkFile(file);
    setBulkResults(null);
  };

  const handleBulkImport = async () => {
    if (!bulkFile || !bulkExpDate || !bulkExpTime) {
      toast.error("Complete todos los campos");
      return;
    }
    setBulkLoading(true);
    try {
      const expDateTime = new Date(`${bulkExpDate}T${bulkExpTime}`).toISOString();
      const formData = new FormData();
      formData.append('file', bulkFile);
      formData.append('expiration_date', expDateTime);
      if (selectedProperty) {
        formData.append('property_id', selectedProperty);
      }
      const response = await axios.post(`${API}/visitors/bulk-import`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBulkResults(response.data);
      toast.success(`${response.data.created} visitantes creados exitosamente`);
      fetchVisitors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al importar archivo");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDownloadPDF = async () => {
    if (!bulkResults?.visitors?.length) return;
    setBulkLoading(true);
    try {
      const visitorIds = bulkResults.visitors.map(v => v.id);
      const response = await axios.post(`${API}/visitors/bulk-pdf`, visitorIds, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'visitantes_qr.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (error) {
      toast.error("Error al generar PDF");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleDownloadSingleQR = async (visitorId, visitorName) => {
    try {
      const response = await axios.get(`${API}/visitors/${visitorId}/qr-image`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${visitorName.replace(/ /g, '_')}_QR.png`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Error al descargar QR");
    }
  };

  const handleBulkDownloadZIP = async () => {
    if (!bulkResults?.visitors?.length) return;
    setBulkLoading(true);
    try {
      const visitorIds = bulkResults.visitors.map(v => v.id);
      const response = await axios.post(`${API}/visitors/bulk-qr-zip`, visitorIds, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'visitantes_QR.zip');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("ZIP descargado");
    } catch (error) {
      toast.error("Error al generar ZIP");
    } finally {
      setBulkLoading(false);
    }
  };

  // Download ALL visitors QR as ZIP
  const handleDownloadAllQRsZIP = async () => {
    if (!visitors?.length) {
      toast.error("No hay visitantes para descargar");
      return;
    }
    setBulkLoading(true);
    try {
      const visitorIds = visitors.map(v => v.id);
      const response = await axios.post(`${API}/visitors/bulk-qr-zip`, visitorIds, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const propertyName = selectedProperty 
        ? properties.find(p => p.id === selectedProperty)?.name || 'visitantes' 
        : 'todos_visitantes';
      link.setAttribute('download', `${propertyName.replace(/\s/g, '_')}_QR.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`ZIP con ${visitorIds.length} QRs descargado`);
    } catch (error) {
      toast.error("Error al generar ZIP");
    } finally {
      setBulkLoading(false);
    }
  };

  // Download ALL visitors QR as PDF
  const handleDownloadAllQRsPDF = async () => {
    if (!visitors?.length) {
      toast.error("No hay visitantes para descargar");
      return;
    }
    setBulkLoading(true);
    try {
      const visitorIds = visitors.map(v => v.id);
      const response = await axios.post(`${API}/visitors/bulk-pdf`, visitorIds, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const propertyName = selectedProperty 
        ? properties.find(p => p.id === selectedProperty)?.name || 'visitantes' 
        : 'todos_visitantes';
      link.setAttribute('download', `${propertyName.replace(/\s/g, '_')}_QR.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF con ${visitorIds.length} QRs descargado`);
    } catch (error) {
      toast.error("Error al generar PDF");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkReset = () => {
    setBulkFile(null);
    setBulkResults(null);
    setBulkExpDate('');
    setBulkExpTime('');
  };

  // Export QR codes to Excel (admin_master only)
  const handleExportQRsToExcel = async () => {
    setBulkLoading(true);
    try {
      const response = await axios.get(`${API}/export/qr-codes-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      }));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `codigos-qr-${today}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Reporte Excel descargado");
    } catch (error) {
      toast.error("Error al generar Excel");
    } finally {
      setBulkLoading(false);
    }
  };

  // Export all QR records to PDF (admin_master only)
  const handleExportQRsToPDF = async () => {
    setBulkLoading(true);
    try {
      const response = await axios.get(`${API}/export/visitors-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `registros-qr-${today}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Reporte PDF de registros descargado");
    } catch (error) {
      toast.error("Error al generar PDF de registros");
    } finally {
      setBulkLoading(false);
    }
  };

  // Export all users to PDF (admin_master only)
  const handleExportUsersToPDF = async () => {
    setBulkLoading(true);
    try {
      const response = await axios.get(`${API}/export/users-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `usuarios-sistema-${today}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Reporte PDF de usuarios descargado");
    } catch (error) {
      toast.error("Error al generar PDF de usuarios");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error("Por favor ingrese un nombre");
      return;
    }

    setLoading(true);
    try {
      // Build start datetime when multi-scan is enabled
      let startDateTime = null;
      if (isMultiScan && (startDate || startTime)) {
        if (startDate && startTime) {
          startDateTime = new Date(`${startDate}T${startTime}`).toISOString();
        } else if (startTime) {
          const today = new Date().toISOString().split('T')[0];
          startDateTime = new Date(`${today}T${startTime}`).toISOString();
        }
      }
      
      // Build expiration datetime
      let expirationDateTime = null;
      if (expirationDate && expirationTime) {
        expirationDateTime = new Date(`${expirationDate}T${expirationTime}`).toISOString();
      } else if (expirationDate) {
        expirationDateTime = new Date(`${expirationDate}T23:59:59`).toISOString();
      } else if (expirationTime) {
        const today = new Date().toISOString().split('T')[0];
        expirationDateTime = new Date(`${today}T${expirationTime}`).toISOString();
      }

      await axios.post(`${API}/visitors`, {
        name: name.trim(),
        visitor_type: visitorType,
        area: area,
        visit_reason: visitReason || null,
        number_of_visitors: numberOfVisitors || 1,
        notes: notes.trim() || null,
        start_time: startDateTime,
        expiration_date: expirationDateTime,
        is_multi_scan: isMultiScan,
        vehicle_plate: vehiclePlate.trim() || null,
        vehicle_brand: vehicleBrand.trim() || null,
        vehicle_color: vehicleColor.trim() || null,
        property_id: selectedProperty || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Visitante creado exitosamente`);
      setName("");
      setVisitorType("visita");
      setArea("ventas");
      setVisitReason("");
      setNumberOfVisitors(1);
      setNotes("");
      setStartDate("");
      setStartTime("");
      setExpirationDate("");
      setExpirationTime("");
      setIsMultiScan(false);
      setVehiclePlate("");
      setVehicleBrand("");
      setVehicleColor("");
      
      // Refresh list immediately and also after a short delay to ensure DB sync
      await fetchVisitors();
      fetchStatistics();
      setTimeout(() => {
        fetchVisitors();
        fetchStatistics();
      }, 500);
    } catch (error) {
      toast.error("Error al crear visitante");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (visitorId) => {
    if (!window.confirm("¿Está seguro de eliminar este visitante?")) {
      return;
    }

    try {
      await axios.delete(`${API}/visitors/${visitorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Visitante eliminado");
      await fetchVisitors();
      fetchStatistics();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' ? detail : 'Error al eliminar visitante';
      toast.error(errorMsg);
    }
  };

  const handleViewQR = (visitor) => {
    setSelectedVisitor(visitor);
    setShowQRModal(true);
  };

  const handleViewNotes = (visitor) => {
    setSelectedVisitorNotes(visitor);
    setShowNotesModal(true);
  };

  const downloadQR = () => {
    const svg = document.getElementById("qr-code-svg");
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    
    // Set canvas size for professional QR card
    const cardWidth = 800;
    const cardHeight = 1000;
    canvas.width = cardWidth;
    canvas.height = cardHeight;
    
    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, cardWidth, cardHeight);
    
    // Load and draw logo
    const logo = new Image();
    logo.crossOrigin = "anonymous";
    logo.onload = () => {
      // Draw logo at top center
      const logoHeight = 80;
      const logoWidth = (logo.width * logoHeight) / logo.height;
      ctx.drawImage(logo, (cardWidth - logoWidth) / 2, 40, logoWidth, logoHeight);
      
      // Title
      ctx.fillStyle = '#1a202c';
      ctx.font = 'bold 32px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('CÓDIGO QR DE ACCESO', cardWidth / 2, 180);
      
      // Line separator
      ctx.strokeStyle = '#667eea';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(150, 210);
      ctx.lineTo(650, 210);
      ctx.stroke();
      
      // Visitor information
      ctx.textAlign = 'left';
      ctx.font = 'bold 24px Inter, sans-serif';
      ctx.fillStyle = '#667eea';
      let yPos = 260;
      
      ctx.fillText('Nombre:', 80, yPos);
      ctx.fillStyle = '#1a202c';
      ctx.font = '24px Inter, sans-serif';
      ctx.fillText(selectedVisitor.name, 80, yPos + 35);
      
      yPos += 90;
      ctx.fillStyle = '#667eea';
      ctx.font = 'bold 24px Inter, sans-serif';
      ctx.fillText('Tipo:', 80, yPos);
      ctx.fillStyle = '#1a202c';
      ctx.font = '24px Inter, sans-serif';
      ctx.fillText(selectedVisitor.visitor_type.toUpperCase(), 80, yPos + 35);
      
      yPos += 90;
      ctx.fillStyle = '#667eea';
      ctx.font = 'bold 24px Inter, sans-serif';
      ctx.fillText('Área:', 80, yPos);
      ctx.fillStyle = '#1a202c';
      ctx.font = '24px Inter, sans-serif';
      ctx.fillText(selectedVisitor.area || 'N/A', 80, yPos + 35);
      
      // Load and draw QR code
      const qrImg = new Image();
      qrImg.onload = () => {
        const qrSize = 350;
        const qrX = (cardWidth - qrSize) / 2;
        const qrY = yPos + 70;
        
        // QR background
        ctx.fillStyle = '#f7fafc';
        ctx.fillRect(qrX - 20, qrY - 20, qrSize + 40, qrSize + 40);
        
        // QR border
        ctx.strokeStyle = '#667eea';
        ctx.lineWidth = 2;
        ctx.strokeRect(qrX - 20, qrY - 20, qrSize + 40, qrSize + 40);
        
        // Draw QR
        ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
        
        // Footer
        ctx.font = '18px Inter, sans-serif';
        ctx.fillStyle = '#718096';
        ctx.textAlign = 'center';
        ctx.fillText('Presente este código al llegar', cardWidth / 2, qrY + qrSize + 70);
        ctx.fillText(`Creado: ${formatDate(selectedVisitor.created_at)}`, cardWidth / 2, qrY + qrSize + 100);
        
        // Download
        const pngFile = canvas.toDataURL("image/png");
        const downloadLink = document.createElement("a");
        downloadLink.download = `QR-${selectedVisitor.name.replace(/\s/g, '_')}.png`;
        downloadLink.href = pngFile;
        downloadLink.click();
      };
      qrImg.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
    };
    logo.src = "/villagroup-logo.png";
  };

  // Function to send QR via email
  const handleSendEmail = async () => {
    if (!emailRecipient || !selectedVisitor) {
      toast.error('Ingrese un email válido');
      return;
    }

    setSendingEmail(true);
    
    try {
      // Generate QR as base64 for email - SMALL size to fit EmailJS 50KB limit
      const svg = document.getElementById("qr-code-svg");
      const svgData = new XMLSerializer().serializeToString(svg);
      
      // Create small canvas to keep base64 under 50KB
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const qrSize = 150; // Small size for email
      canvas.width = qrSize;
      canvas.height = qrSize;
      
      const qrImg = new Image();
      
      await new Promise((resolve, reject) => {
        qrImg.onload = () => {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, qrSize, qrSize);
          ctx.drawImage(qrImg, 0, 0, qrSize, qrSize);
          resolve();
        };
        qrImg.onerror = reject;
        qrImg.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
      });
      
      // Use JPEG with compression to reduce size further
      const qrBase64 = canvas.toDataURL("image/jpeg", 0.8);
      
      // Get property name
      const propertyName = properties.find(p => p.id === selectedVisitor.property_id)?.name || 'Control de Accesos';
      
      // Send email
      const result = await sendQRCodeEmail({
        to_email: emailRecipient,
        visitor_name: selectedVisitor.name,
        property_name: propertyName,
        area: selectedVisitor.area || 'General',
        reason: selectedVisitor.visit_reason || 'Visita',
        visit_date: formatDate(selectedVisitor.created_at),
        expiration_date: selectedVisitor.expiration_date ? formatDate(selectedVisitor.expiration_date) : 'Sin expiración',
        qr_code_base64: qrBase64
      });

      if (result.success) {
        toast.success(result.message);
        setShowEmailModal(false);
        setEmailRecipient('');
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Error al enviar el email');
    } finally {
      setSendingEmail(false);
    }
  };

  const openEmailModal = () => {
    setEmailRecipient('');
    setShowEmailModal(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="admin-container" data-testid="admin-dashboard">
      <div className="admin-header">
        <div className="header-left">
          <h1 data-testid="admin-title">
            {isAdminMaster ? 'Panel de Admin Master' : (isAdmin ? 'Panel de Administrador' : 'Panel de Usuario')}
          </h1>
          <p className="user-info">Usuario: <strong>{user?.username}</strong> | Departamento: <strong>{user?.department}</strong></p>
          
          {/* Property Selector */}
          {properties.length > 1 && (
            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontWeight: '600', color: '#374151' }}>🏨 Propiedad:</label>
              <select
                value={selectedProperty}
                onChange={(e) => setSelectedProperty(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '2px solid #667eea',
                  background: '#f3f4f6',
                  fontWeight: '500',
                  cursor: 'pointer',
                  minWidth: '250px'
                }}
                data-testid="property-selector"
              >
                {isAdminMaster && <option value="">Todas las propiedades</option>}
                {properties.map(prop => (
                  <option key={prop.id} value={prop.id}>{prop.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="header-actions">
          {isAdmin && (
            <>
              <Link to="/users" className="users-button" data-testid="users-btn">
                👥 Gestionar Usuarios
              </Link>
              <Link to="/history" className="history-button" data-testid="history-btn">
                📋 Ver Historial
              </Link>
              <Link to="/support-messages" className="support-button" data-testid="support-messages-btn">
                💬 Mensajes de Soporte
              </Link>
              {isAdminMaster && (
                <Link to="/properties" className="properties-button" data-testid="properties-btn" style={{
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  color: 'white',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  🏢 Propiedades
                </Link>
              )}
            </>
          )}
          <Link to="/support" className="help-button" data-testid="help-btn">
            ❓ Ayuda
          </Link>
          <button onClick={logout} className="logout-button" data-testid="logout-btn">
            Cerrar Sesión
          </button>
        </div>
      </div>

      {/* Statistics Cards - Only for admins */}
      {isAdmin && statistics && (
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto 20px auto',
          padding: '0 20px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '15px',
            marginBottom: '20px'
          }}>
            {/* Total Visitors */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '12px',
              padding: '20px',
              color: 'white',
              textAlign: 'center'
            }} data-testid="stat-total">
              <div style={{ fontSize: '32px', fontWeight: '700' }}>{statistics.total}</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Total Visitantes</div>
            </div>
            
            {/* Pending */}
            <div style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              borderRadius: '12px',
              padding: '20px',
              color: 'white',
              textAlign: 'center'
            }} data-testid="stat-pending">
              <div style={{ fontSize: '32px', fontWeight: '700' }}>{statistics.pending}</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Pendientes</div>
            </div>
            
            {/* Used */}
            <div style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '12px',
              padding: '20px',
              color: 'white',
              textAlign: 'center'
            }} data-testid="stat-used">
              <div style={{ fontSize: '32px', fontWeight: '700' }}>{statistics.used}</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Utilizados</div>
            </div>
            
            {/* Expired */}
            <div style={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              borderRadius: '12px',
              padding: '20px',
              color: 'white',
              textAlign: 'center'
            }} data-testid="stat-expired">
              <div style={{ fontSize: '32px', fontWeight: '700' }}>{statistics.expired}</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Expirados</div>
            </div>
            
            {/* Today */}
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              borderRadius: '12px',
              padding: '20px',
              color: 'white',
              textAlign: 'center'
            }} data-testid="stat-today">
              <div style={{ fontSize: '32px', fontWeight: '700' }}>{statistics.today}</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Hoy</div>
            </div>
            
            {/* This Week */}
            <div style={{
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              borderRadius: '12px',
              padding: '20px',
              color: 'white',
              textAlign: 'center'
            }} data-testid="stat-week">
              <div style={{ fontSize: '32px', fontWeight: '700' }}>{statistics.this_week}</div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>Esta Semana</div>
            </div>
            
            {/* Total Users Card - Only for admins */}
            {isAdmin && (
              <div style={{
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                borderRadius: '12px',
                padding: '20px',
                color: 'white',
                textAlign: 'center'
              }} data-testid="stat-users">
                <div style={{ fontSize: '32px', fontWeight: '700' }}>{statistics.total_users || 0}</div>
                <div style={{ fontSize: '14px', opacity: 0.9 }}>Usuarios</div>
              </div>
            )}
          </div>
          
          {/* Download All QRs Buttons */}
          {isAdmin && visitors.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              marginTop: '20px',
              padding: '16px',
              background: '#f8fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              <span style={{ display: 'flex', alignItems: 'center', fontWeight: '600', color: '#374151', marginRight: '10px' }}>
                📥 Descargar todos los QRs ({visitors.length}):
              </span>
              <button 
                onClick={handleDownloadAllQRsZIP} 
                disabled={bulkLoading}
                style={{
                  padding: '10px 20px',
                  background: bulkLoading ? '#94a3b8' : '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: bulkLoading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9rem'
                }}
                data-testid="download-all-zip"
              >
                {bulkLoading ? '⏳ Generando...' : '🖼️ ZIP (imágenes)'}
              </button>
              <button 
                onClick={handleDownloadAllQRsPDF} 
                disabled={bulkLoading}
                style={{
                  padding: '10px 20px',
                  background: bulkLoading ? '#94a3b8' : '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: bulkLoading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  fontSize: '0.9rem'
                }}
                data-testid="download-all-pdf"
              >
                {bulkLoading ? '⏳ Generando...' : '📄 PDF (documento)'}
              </button>
              
              {/* Export to Excel - Only for admin_master */}
              {user?.role === 'admin_master' && (
                <>
                  <button 
                    onClick={handleExportQRsToExcel} 
                    disabled={bulkLoading}
                    style={{
                      padding: '10px 20px',
                      background: bulkLoading ? '#94a3b8' : '#059669',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: bulkLoading ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem'
                    }}
                    data-testid="export-excel-btn"
                  >
                    {bulkLoading ? '⏳ Generando...' : '📊 Exportar a Excel'}
                  </button>
                  <button
                    onClick={handleExportQRsToPDF}
                    disabled={bulkLoading}
                    style={{
                      padding: '10px 20px',
                      background: bulkLoading ? '#94a3b8' : '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: bulkLoading ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem'
                    }}
                    data-testid="export-visitors-pdf-btn"
                  >
                    {bulkLoading ? '⏳ Generando...' : '📄 Registros PDF'}
                  </button>
                  <button
                    onClick={handleExportUsersToPDF}
                    disabled={bulkLoading}
                    style={{
                      padding: '10px 20px',
                      background: bulkLoading ? '#94a3b8' : '#7c3aed',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: bulkLoading ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem'
                    }}
                    data-testid="export-users-pdf-btn"
                  >
                    {bulkLoading ? '⏳ Generando...' : '👥 Usuarios PDF'}
                  </button>
                </>
              )}
            </div>
          )}
          
          {/* Area Breakdown */}
          {statistics.by_area && statistics.by_area.length > 0 && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 15px 0', color: '#1f2937', fontSize: '16px' }}>Por Área</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {statistics.by_area.slice(0, 6).map((item, idx) => (
                  <div key={`area-${idx}`} style={{
                    background: '#f3f4f6',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontSize: '14px'
                  }}>
                    <strong>{item.area}:</strong> {item.count}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: '0',
        marginBottom: '20px',
        borderBottom: '2px solid #e5e7eb',
        maxWidth: '1200px',
        margin: '0 auto 20px auto',
        padding: '0 20px'
      }}>
        <button
          onClick={() => setActiveView('form')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: activeView === 'form' ? '#667eea' : 'rgba(102, 126, 234, 0.1)',
            color: activeView === 'form' ? 'white' : '#1f2937',
            cursor: 'pointer',
            fontWeight: '600',
            borderRadius: '8px 8px 0 0',
            transition: 'all 0.2s'
          }}
          data-testid="tab-create-visitor"
        >
          ➕ Crear Visita
        </button>
        <button
          onClick={() => setActiveView('notes')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: activeView === 'notes' ? '#667eea' : 'rgba(102, 126, 234, 0.1)',
            color: activeView === 'notes' ? 'white' : '#1f2937',
            cursor: 'pointer',
            fontWeight: '600',
            borderRadius: '8px 8px 0 0',
            transition: 'all 0.2s'
          }}
          data-testid="tab-visit-notes"
        >
          📝 Notas de Visitas
        </button>
        {isAdmin && (
          <button
            onClick={() => setActiveView('bulk')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeView === 'bulk' ? '#667eea' : 'rgba(102, 126, 234, 0.1)',
              color: activeView === 'bulk' ? 'white' : '#1f2937',
              cursor: 'pointer',
              fontWeight: '600',
              borderRadius: '8px 8px 0 0',
              transition: 'all 0.2s'
            }}
            data-testid="tab-bulk-import"
          >
            📂 Importar Excel
          </button>
        )}
      </div>

      {activeView === 'form' && (
        <div className="admin-content">
          <div className="form-section" data-testid="create-visitor-form">
            <h2>Crear Nueva Visita</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Nombre</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ingrese el nombre"
                  data-testid="visitor-name-input"
                />
              </div>

              {/* Property Selection for visitor */}
              {properties.length > 1 && (
                <div className="form-group">
                  <label htmlFor="visitorProperty" style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <span>🏨</span> Propiedad / Hotel
                  </label>
                  <select
                    id="visitorProperty"
                    value={selectedProperty}
                    onChange={(e) => setSelectedProperty(e.target.value)}
                    data-testid="visitor-property-select"
                    style={{
                      border: '2px solid #667eea',
                      background: '#f0f4ff'
                    }}
                  >
                    {properties.map((prop) => (
                      <option key={prop.id} value={prop.id}>
                        {prop.name} ({prop.code})
                      </option>
                    ))}
                  </select>
                  <p style={{fontSize: '12px', color: '#6b7280', marginTop: '4px'}}>
                    El QR será válido solo para esta propiedad
                  </p>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="visitReason">Motivo de la Visita</label>
                <select
                  id="visitReason"
                  value={visitReason}
                  onChange={(e) => setVisitReason(e.target.value)}
                  data-testid="visit-reason-input"
                >
                  <option value="">Seleccione un motivo</option>
                  {availableReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="area">Área</label>
                <select
                  id="area"
                  value={area}
                  onChange={(e) => setArea(e.target.value)}
                  data-testid="visitor-area-select"
                >
                  {availableAreas.map((areaOption) => (
                    <option key={areaOption} value={areaOption}>
                      {areaOption}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="numberOfVisitors">Número de Visitas</label>
                <input
                  id="numberOfVisitors"
                  type="number"
                  min="1"
                  value={numberOfVisitors}
                  onChange={(e) => setNumberOfVisitors(parseInt(e.target.value) || 1)}
                  placeholder="1"
                  data-testid="number-of-visitors-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notas</label>
                <textarea
                  id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Agregar notas adicionales..."
                data-testid="notes-input"
                rows="3"
              />
            </div>

            {/* Multi-scan options - Available for all users */}
            <div style={{
              background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
              border: '2px solid #0ea5e9',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div className="form-group" style={{marginBottom: '12px'}}>
                <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
                  <input
                    type="checkbox"
                    checked={isMultiScan}
                    onChange={(e) => setIsMultiScan(e.target.checked)}
                    style={{width: '20px', height: '20px'}}
                    data-testid="multi-scan-checkbox"
                  />
                  <span style={{fontWeight: '600', color: '#0369a1'}}>
                    Permitir múltiples escaneos (entrada/salida)
                  </span>
                </label>
                <p style={{fontSize: '12px', color: '#0369a1', margin: '4px 0 0 30px'}}>
                  El invitado podrá entrar y salir múltiples veces durante el evento
                </p>
              </div>

              {isMultiScan && (
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                  <div className="form-group" style={{marginBottom: '0'}}>
                    <label htmlFor="startDate" style={{color: '#0369a1'}}>Fecha de Inicio</label>
                    <input
                      id="startDate"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      data-testid="start-date-input"
                      min={new Date().toISOString().split('T')[0]}
                      style={{borderColor: '#0ea5e9'}}
                    />
                  </div>
                  <div className="form-group" style={{marginBottom: '0'}}>
                    <label htmlFor="startTime" style={{color: '#0369a1'}}>Hora de Inicio</label>
                    <input
                      id="startTime"
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      data-testid="start-time-input"
                      style={{borderColor: '#0ea5e9'}}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="expirationDate">Fecha de Caducidad del QR <span style={{fontSize: '12px', color: '#666'}}>(opcional)</span></label>
              <input
                id="expirationDate"
                type="date"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
                data-testid="expiration-date-input"
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="form-group">
              <label htmlFor="expirationTime">Hora de Caducidad <span style={{fontSize: '12px', color: '#666'}}>(si solo pones hora, caduca hoy)</span></label>
              <input
                id="expirationTime"
                type="time"
                value={expirationTime}
                onChange={(e) => setExpirationTime(e.target.value)}
                data-testid="expiration-time-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="vehiclePlate">Placa de Vehículo</label>
              <input
                id="vehiclePlate"
                type="text"
                value={vehiclePlate}
                onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())}
                placeholder="Ej: ABC-1234"
                data-testid="vehicle-plate-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="vehicleBrand">Marca de Vehículo</label>
              <input
                id="vehicleBrand"
                type="text"
                value={vehicleBrand}
                onChange={(e) => setVehicleBrand(e.target.value)}
                placeholder="Ej: Toyota, Ford, Honda"
                data-testid="vehicle-brand-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="vehicleColor">Color de Vehículo</label>
              <input
                id="vehicleColor"
                type="text"
                value={vehicleColor}
                onChange={(e) => setVehicleColor(e.target.value)}
                placeholder="Ej: Blanco, Negro, Rojo"
                data-testid="vehicle-color-input"
              />
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={loading}
              data-testid="create-visitor-btn"
            >
              {loading ? "Creando..." : "Crear Visitante"}
            </button>
          </form>
        </div>

        <div className="list-section" data-testid="visitors-list">
          <h2>Visitantes Registrados ({visitors.length})</h2>
          {visitors.length === 0 ? (
            <div className="no-visitors">
              <div className="empty-state-icon">📋</div>
              <p>No hay visitantes registrados</p>
            </div>
          ) : (
            visitors.map((visitor) => (
              <div key={visitor.id} className="visitor-card" data-testid={`visitor-card-${visitor.id}`}>
                <div className="visitor-info">
                  <div>
                    <div className="visitor-name" data-testid={`visitor-name-${visitor.id}`}>
                      {visitor.name}
                    </div>
                    <div className="visitor-tags">
                      <span className="visitor-type" data-testid={`visitor-type-${visitor.id}`}>
                        {visitor.visitor_type}
                      </span>
                      <span className="area-badge" data-testid={`visitor-area-${visitor.id}`}>
                        {visitor.area}
                      </span>
                      {visitor.notes && (
                        <span style={{
                          background: '#fef3c7',
                          color: '#92400e',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          📝 Con notas
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    className={`visitor-status ${
                      visitor.status === "pending" ? "status-pending" : "status-used"
                    }`}
                    data-testid={`visitor-status-${visitor.id}`}
                  >
                    {visitor.status === "pending" ? "Pendiente" : "Utilizado"}
                  </div>
                </div>
                
                <div className="visitor-meta">
                  <p data-testid={`visitor-created-${visitor.id}`}>
                    Creado: {formatDate(visitor.created_at)}
                  </p>
                  {visitor.scanned_at && (
                    <p data-testid={`visitor-scanned-${visitor.id}`}>
                      Escaneado: {formatDate(visitor.scanned_at)}
                    </p>
                  )}
                  {(visitor.vehicle_plate || visitor.vehicle_brand || visitor.vehicle_color) && (
                    <p data-testid={`visitor-vehicle-${visitor.id}`}>
                      <strong>Vehículo:</strong> {visitor.vehicle_brand || ''} {visitor.vehicle_color ? `${visitor.vehicle_color}` : ''} {visitor.vehicle_plate ? `[${visitor.vehicle_plate}]` : ''}
                    </p>
                  )}
                </div>

                <div className="visitor-actions">
                  <button
                    className="btn-view-qr"
                    onClick={() => handleViewQR(visitor)}
                    data-testid={`view-qr-btn-${visitor.id}`}
                  >
                    Ver QR
                  </button>
                  {visitor.notes && (
                    <button
                      style={{
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px'
                      }}
                      onClick={() => handleViewNotes(visitor)}
                      data-testid={`view-notes-btn-${visitor.id}`}
                    >
                      Ver Notas
                    </button>
                  )}
                  <button
                    className="btn-delete"
                    onClick={() => handleDelete(visitor.id)}
                    data-testid={`delete-visitor-btn-${visitor.id}`}
                  >
                    Eliminar
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      )}

      {/* Notes View */}
      {activeView === 'notes' && (
        <div className="admin-content">
          <div className="form-section" style={{maxWidth: '100%'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
              <h2 style={{margin: 0}}>📝 Notas de Visitas</h2>
              <div style={{display: 'flex', gap: '10px'}}>
                <select
                  value={notesFilter}
                  onChange={(e) => setNotesFilter(e.target.value)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                  data-testid="notes-filter-select"
                >
                  <option value="all">Todas las visitas ({visitors.length})</option>
                  <option value="with-notes">Solo con notas ({visitors.filter(v => v.notes && v.notes.trim()).length})</option>
                  <option value="pending">Pendientes ({visitors.filter(v => v.status === 'pending').length})</option>
                  <option value="used">Utilizados ({visitors.filter(v => v.status === 'used').length})</option>
                </select>
              </div>
            </div>
            
            <p style={{color: '#6b7280', marginBottom: '20px'}}>
              Consulta las notas agregadas a cada visita. Haz clic en una tarjeta para ver los detalles completos.
            </p>

            {filteredVisitors.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px 20px',
                background: '#f9fafb',
                borderRadius: '12px'
              }}>
                <div style={{fontSize: '48px', marginBottom: '16px'}}>📋</div>
                <p style={{color: '#6b7280', fontSize: '16px'}}>
                  {notesFilter === 'with-notes' 
                    ? 'No hay visitas con notas registradas'
                    : 'No hay visitas que mostrar'
                  }
                </p>
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                gap: '16px'
              }}>
                {filteredVisitors.map((visitor) => (
                  <div 
                    key={visitor.id}
                    onClick={() => handleViewNotes(visitor)}
                    style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '16px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      border: visitor.notes ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      ':hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }
                    }}
                    data-testid={`notes-card-${visitor.id}`}
                  >
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px'}}>
                      <div>
                        <h3 style={{margin: '0 0 4px 0', fontSize: '16px', fontWeight: '600', color: '#1f2937'}}>
                          {visitor.name}
                        </h3>
                        <p style={{margin: 0, fontSize: '13px', color: '#6b7280'}}>
                          {visitor.area} • {visitor.visit_reason || visitor.visitor_type}
                        </p>
                      </div>
                      <span style={{
                        background: visitor.status === 'pending' ? '#fef3c7' : '#d1fae5',
                        color: visitor.status === 'pending' ? '#92400e' : '#065f46',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        {visitor.status === 'pending' ? 'Pendiente' : 'Utilizado'}
                      </span>
                    </div>
                    
                    <div style={{
                      background: visitor.notes ? '#fffbeb' : '#f9fafb',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '12px',
                      minHeight: '60px'
                    }}>
                      {visitor.notes ? (
                        <p style={{
                          margin: 0,
                          fontSize: '14px',
                          color: '#374151',
                          lineHeight: '1.5',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {visitor.notes}
                        </p>
                      ) : (
                        <p style={{margin: 0, fontSize: '14px', color: '#9ca3af', fontStyle: 'italic'}}>
                          Sin notas registradas
                        </p>
                      )}
                    </div>
                    
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <span style={{fontSize: '12px', color: '#9ca3af'}}>
                        {formatDate(visitor.created_at)}
                      </span>
                      {visitor.number_of_visitors > 1 && (
                        <span style={{
                          background: '#e0f2fe',
                          color: '#0369a1',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: '600'
                        }}>
                          👥 {visitor.number_of_visitors} personas
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk Import View - Admin Only */}
      {activeView === 'bulk' && isAdmin && (
        <div style={{maxWidth: '900px', margin: '0 auto', padding: '0 20px'}}>
          <div style={{background: 'white', borderRadius: '12px', padding: '30px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)'}}>
            <h2 style={{margin: '0 0 8px 0', color: '#1f2937', fontSize: '1.5rem'}}>Importacion Masiva desde Excel</h2>
            <p style={{color: '#6b7280', margin: '0 0 24px 0', fontSize: '0.9rem'}}>
              Sube un archivo Excel con las columnas: <strong>Nombre</strong>, <strong>Motivo</strong>, <strong>Area</strong>
            </p>

            <div style={{border: '2px dashed #d1d5db', borderRadius: '10px', padding: '30px', textAlign: 'center', marginBottom: '20px', background: bulkFile ? '#f0fdf4' : '#fafafa'}}>
              {!bulkFile ? (
                <>
                  <div style={{fontSize: '2.5rem', marginBottom: '10px'}}>📂</div>
                  <p style={{color: '#6b7280', margin: '0 0 12px 0'}}>Arrastra tu archivo Excel o haz clic para seleccionar</p>
                  <input type="file" accept=".xlsx,.xls" onChange={handleBulkFileChange} style={{display: 'none'}} id="bulk-file-input" data-testid="bulk-file-input" />
                  <label htmlFor="bulk-file-input" style={{display: 'inline-block', padding: '10px 24px', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem'}}>Seleccionar Archivo</label>
                </>
              ) : (
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px'}}>
                  <span style={{fontSize: '1.5rem'}}>✅</span>
                  <div>
                    <p style={{margin: 0, fontWeight: '600', color: '#166534'}}>{bulkFile.name}</p>
                    <p style={{margin: 0, fontSize: '0.8rem', color: '#6b7280'}}>{(bulkFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button onClick={handleBulkReset} style={{padding: '6px 14px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.8rem'}} data-testid="bulk-reset-btn">Cambiar</button>
                </div>
              )}
            </div>

            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px'}}>
              <div>
                <label style={{display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem'}}>Fecha de Expiracion</label>
                <input type="date" value={bulkExpDate} onChange={(e) => setBulkExpDate(e.target.value)} style={{width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box'}} data-testid="bulk-exp-date" />
              </div>
              <div>
                <label style={{display: 'block', marginBottom: '6px', fontWeight: '600', color: '#374151', fontSize: '0.9rem'}}>Hora de Expiracion</label>
                <input type="time" value={bulkExpTime} onChange={(e) => setBulkExpTime(e.target.value)} style={{width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box'}} data-testid="bulk-exp-time" />
              </div>
            </div>

            <button onClick={handleBulkImport} disabled={!bulkFile || !bulkExpDate || !bulkExpTime || bulkLoading}
              style={{width: '100%', padding: '14px', background: (!bulkFile || !bulkExpDate || !bulkExpTime || bulkLoading) ? '#d1d5db' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', borderRadius: '10px', cursor: (!bulkFile || !bulkExpDate || !bulkExpTime || bulkLoading) ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '1rem'}} data-testid="bulk-import-btn">
              {bulkLoading ? 'Procesando...' : 'Importar y Generar QR'}
            </button>

            {bulkResults && (
              <div style={{marginTop: '24px'}}>
                <div style={{display: 'flex', gap: '12px', marginBottom: '16px'}}>
                  <div style={{flex: 1, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', padding: '16px', textAlign: 'center'}}>
                    <div style={{fontSize: '1.8rem', fontWeight: '700', color: '#166534'}}>{bulkResults.created}</div>
                    <div style={{color: '#166534', fontSize: '0.85rem'}}>Creados</div>
                  </div>
                  {bulkResults.errors > 0 && (
                    <div style={{flex: 1, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '16px', textAlign: 'center'}}>
                      <div style={{fontSize: '1.8rem', fontWeight: '700', color: '#dc2626'}}>{bulkResults.errors}</div>
                      <div style={{color: '#dc2626', fontSize: '0.85rem'}}>Errores</div>
                    </div>
                  )}
                </div>

                <div style={{maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', marginBottom: '16px'}}>
                  <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem'}}>
                    <thead>
                      <tr style={{background: '#f9fafb', position: 'sticky', top: 0}}>
                        <th style={{padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb'}}>Nombre</th>
                        <th style={{padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb'}}>Area</th>
                        <th style={{padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb'}}>Motivo</th>
                        <th style={{padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb'}}>QR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkResults.visitors.map((v) => (
                        <tr key={v.id} style={{borderBottom: '1px solid #f3f4f6'}}>
                          <td style={{padding: '8px 12px'}}>{v.name}</td>
                          <td style={{padding: '8px 12px'}}>{v.area}</td>
                          <td style={{padding: '8px 12px'}}>{v.visit_reason || '-'}</td>
                          <td style={{padding: '8px 12px', textAlign: 'center'}}>
                            <button onClick={() => handleDownloadSingleQR(v.id, v.name)} style={{padding: '4px 12px', background: '#667eea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600'}}>Descargar PNG</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap'}}>
                  <button onClick={handleBulkDownloadZIP} disabled={bulkLoading} style={{flex: 1, minWidth: '200px', padding: '12px', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem'}} data-testid="bulk-download-zip">
                    {bulkLoading ? 'Generando...' : '🖼️ Descargar ZIP (imagenes individuales)'}
                  </button>
                  <button onClick={handleBulkDownloadPDF} disabled={bulkLoading} style={{flex: 1, minWidth: '200px', padding: '12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem'}} data-testid="bulk-download-pdf">
                    {bulkLoading ? 'Generando...' : '📄 Descargar PDF (todas en uno)'}
                  </button>
                  <button onClick={handleBulkReset} style={{padding: '12px 20px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500', fontSize: '0.9rem'}} data-testid="bulk-new-import">Nueva importacion</button>
                </div>
              </div>
            )}

            <div style={{marginTop: '24px', padding: '16px', background: '#eff6ff', borderRadius: '10px', border: '1px solid #bfdbfe'}}>
              <p style={{margin: '0 0 8px 0', fontWeight: '600', color: '#1e40af', fontSize: '0.9rem'}}>Formato del archivo Excel:</p>
              <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', background: 'white', borderRadius: '6px', overflow: 'hidden'}}>
                <thead><tr style={{background: '#dbeafe'}}><th style={{padding: '8px', textAlign: 'left', border: '1px solid #bfdbfe'}}>Nombre</th><th style={{padding: '8px', textAlign: 'left', border: '1px solid #bfdbfe'}}>Motivo</th><th style={{padding: '8px', textAlign: 'left', border: '1px solid #bfdbfe'}}>Area</th></tr></thead>
                <tbody><tr><td style={{padding: '8px', border: '1px solid #e5e7eb', color: '#6b7280'}}>Juan Perez</td><td style={{padding: '8px', border: '1px solid #e5e7eb', color: '#6b7280'}}>Visita</td><td style={{padding: '8px', border: '1px solid #e5e7eb', color: '#6b7280'}}>Ventas</td></tr></tbody>
              </table>
              <p style={{margin: '8px 0 0 0', fontSize: '0.75rem', color: '#6b7280'}}>Solo la columna &quot;Nombre&quot; es obligatoria.</p>
            </div>
          </div>
        </div>
      )}

      {/* QR Modal */}
      <Dialog open={showQRModal} onOpenChange={setShowQRModal}>
        <DialogContent className="sm:max-w-md" data-testid="qr-modal">
          <DialogHeader>
            <DialogTitle>Código QR - {selectedVisitor?.name}</DialogTitle>
            <DialogDescription>
              Escanee este código QR para validar el acceso del visitante
            </DialogDescription>
          </DialogHeader>
          <div className="qr-modal-content">
            {selectedVisitor && (
              <>
                <div className="visitor-details">
                  <p>
                    <strong>Nombre:</strong> {selectedVisitor.name}
                  </p>
                  <p>
                    <strong>Tipo:</strong> {selectedVisitor.visitor_type}
                  </p>
                  <p>
                    <strong>Área:</strong> {selectedVisitor.area}
                  </p>
                  {selectedVisitor.vehicle_plate && (
                    <p>
                      <strong>Placa:</strong> {selectedVisitor.vehicle_plate}
                    </p>
                  )}
                  {selectedVisitor.vehicle_brand && (
                    <p>
                      <strong>Marca:</strong> {selectedVisitor.vehicle_brand}
                    </p>
                  )}
                  {selectedVisitor.vehicle_color && (
                    <p>
                      <strong>Color:</strong> {selectedVisitor.vehicle_color}
                    </p>
                  )}
                  {selectedVisitor.visit_reason && (
                    <p>
                      <strong>Motivo:</strong> {selectedVisitor.visit_reason}
                    </p>
                  )}
                  <p>
                    <strong>Estado:</strong>{" "}
                    {selectedVisitor.status === "pending" ? "Pendiente" : "Utilizado"}
                  </p>
                  <p>
                    <strong>Creado:</strong> {formatDate(selectedVisitor.created_at)}
                  </p>
                </div>

                <div className="qr-code-wrapper">
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <div style={{ 
                      position: 'absolute', 
                      top: '-60px', 
                      left: '50%', 
                      transform: 'translateX(-50%)',
                      background: 'white',
                      padding: '10px',
                      borderRadius: '8px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      <img 
                        src="/villagroup-logo.png" 
                        alt="VillaGroup Logo" 
                        style={{ height: '40px', width: 'auto' }}
                      />
                    </div>
                    <QRCode
                      id="qr-code-svg"
                      value={selectedVisitor.qr_code}
                      size={256}
                      data-testid="qr-code-display"
                    />
                  </div>
                </div>

                <div className="qr-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    className="btn-download"
                    onClick={downloadQR}
                    data-testid="download-qr-btn"
                  >
                    📥 Descargar QR
                  </button>
                  <button
                    className="btn-download"
                    onClick={openEmailModal}
                    data-testid="send-email-btn"
                    style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                  >
                    ✉️ Enviar por Email
                  </button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Notes Detail Modal */}
      <Dialog open={showNotesModal} onOpenChange={setShowNotesModal}>
        <DialogContent style={{maxWidth: '600px'}}>
          <DialogHeader>
            <DialogTitle>📝 Detalles de la Visita</DialogTitle>
            <DialogDescription>
              Información completa y notas del visitante
            </DialogDescription>
          </DialogHeader>
          
          {selectedVisitorNotes && (
            <div style={{padding: '10px 0'}}>
              {/* Visitor Info */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginBottom: '20px'
              }}>
                <div>
                  <label style={{fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase'}}>Nombre</label>
                  <p style={{margin: '4px 0 0 0', fontSize: '16px', fontWeight: '600', color: '#1f2937'}}>{selectedVisitorNotes.name}</p>
                </div>
                <div>
                  <label style={{fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase'}}>Estado</label>
                  <p style={{margin: '4px 0 0 0'}}>
                    <span style={{
                      background: selectedVisitorNotes.status === 'pending' ? '#fef3c7' : '#d1fae5',
                      color: selectedVisitorNotes.status === 'pending' ? '#92400e' : '#065f46',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '600'
                    }}>
                      {selectedVisitorNotes.status === 'pending' ? 'Pendiente' : 'Utilizado'}
                    </span>
                  </p>
                </div>
                <div>
                  <label style={{fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase'}}>Área</label>
                  <p style={{margin: '4px 0 0 0', fontSize: '14px', color: '#374151'}}>{selectedVisitorNotes.area}</p>
                </div>
                <div>
                  <label style={{fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase'}}>Motivo</label>
                  <p style={{margin: '4px 0 0 0', fontSize: '14px', color: '#374151'}}>{selectedVisitorNotes.visit_reason || selectedVisitorNotes.visitor_type}</p>
                </div>
                <div>
                  <label style={{fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase'}}>Fecha de Creación</label>
                  <p style={{margin: '4px 0 0 0', fontSize: '14px', color: '#374151'}}>{formatDate(selectedVisitorNotes.created_at)}</p>
                </div>
                <div>
                  <label style={{fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase'}}>Número de Personas</label>
                  <p style={{margin: '4px 0 0 0', fontSize: '14px', color: '#374151'}}>{selectedVisitorNotes.number_of_visitors || 1}</p>
                </div>
                {selectedVisitorNotes.scanned_at && (
                  <div style={{gridColumn: '1 / -1'}}>
                    <label style={{fontSize: '12px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase'}}>Escaneado</label>
                    <p style={{margin: '4px 0 0 0', fontSize: '14px', color: '#374151'}}>{formatDate(selectedVisitorNotes.scanned_at)}</p>
                  </div>
                )}
              </div>

              {/* Vehicle Info */}
              {(selectedVisitorNotes.vehicle_plate || selectedVisitorNotes.vehicle_brand || selectedVisitorNotes.vehicle_color) && (
                <div style={{
                  background: '#f0f9ff',
                  borderRadius: '12px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{margin: '0 0 12px 0', color: '#0369a1', fontSize: '14px', fontWeight: '600'}}>🚗 Información del Vehículo</h4>
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px'}}>
                    {selectedVisitorNotes.vehicle_plate && (
                      <div>
                        <label style={{fontSize: '11px', color: '#6b7280'}}>Placa</label>
                        <p style={{margin: '2px 0 0 0', fontWeight: '600'}}>{selectedVisitorNotes.vehicle_plate}</p>
                      </div>
                    )}
                    {selectedVisitorNotes.vehicle_brand && (
                      <div>
                        <label style={{fontSize: '11px', color: '#6b7280'}}>Marca</label>
                        <p style={{margin: '2px 0 0 0', fontWeight: '600'}}>{selectedVisitorNotes.vehicle_brand}</p>
                      </div>
                    )}
                    {selectedVisitorNotes.vehicle_color && (
                      <div>
                        <label style={{fontSize: '11px', color: '#6b7280'}}>Color</label>
                        <p style={{margin: '2px 0 0 0', fontWeight: '600'}}>{selectedVisitorNotes.vehicle_color}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notes Section */}
              <div style={{
                background: selectedVisitorNotes.notes ? '#fffbeb' : '#f9fafb',
                border: selectedVisitorNotes.notes ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '20px'
              }}>
                <h4 style={{
                  margin: '0 0 12px 0', 
                  color: selectedVisitorNotes.notes ? '#92400e' : '#6b7280', 
                  fontSize: '14px', 
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  📝 Notas
                </h4>
                {selectedVisitorNotes.notes ? (
                  <p style={{
                    margin: 0,
                    fontSize: '15px',
                    color: '#374151',
                    lineHeight: '1.6',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {selectedVisitorNotes.notes}
                  </p>
                ) : (
                  <p style={{margin: 0, fontSize: '14px', color: '#9ca3af', fontStyle: 'italic'}}>
                    No se agregaron notas para esta visita
                  </p>
                )}
              </div>

              {/* Actions */}
              <div style={{display: 'flex', gap: '12px', marginTop: '20px', justifyContent: 'flex-end'}}>
                <button
                  onClick={() => setShowNotesModal(false)}
                  style={{
                    padding: '10px 20px',
                    background: '#f3f4f6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Cerrar
                </button>
                <button
                  onClick={() => {
                    setSelectedVisitor(selectedVisitorNotes);
                    setShowNotesModal(false);
                    setShowQRModal(true);
                  }}
                  style={{
                    padding: '10px 20px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Ver QR
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="sm:max-w-md" data-testid="email-modal">
          <DialogHeader>
            <DialogTitle>✉️ Enviar QR por Email</DialogTitle>
            <DialogDescription>
              Ingrese el correo electrónico del destinatario
            </DialogDescription>
          </DialogHeader>
          <div style={{ padding: '20px 0' }}>
            {selectedVisitor && (
              <div style={{ marginBottom: '20px', padding: '15px', background: '#f8fafc', borderRadius: '8px' }}>
                <p style={{ margin: '0 0 5px 0', fontWeight: '600' }}>Visitante: {selectedVisitor.name}</p>
                <p style={{ margin: 0, fontSize: '14px', color: '#64748b' }}>Área: {selectedVisitor.area}</p>
              </div>
            )}
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>
                Email del destinatario:
              </label>
              <input
                type="email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder="ejemplo@correo.com"
                data-testid="email-input"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '16px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowEmailModal(false)}
                style={{
                  padding: '10px 20px',
                  background: '#f1f5f9',
                  color: '#64748b',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailRecipient}
                data-testid="confirm-send-email-btn"
                style={{
                  padding: '10px 20px',
                  background: sendingEmail || !emailRecipient 
                    ? '#94a3b8' 
                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: sendingEmail || !emailRecipient ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  minWidth: '120px'
                }}
              >
                {sendingEmail ? '⏳ Enviando...' : '📧 Enviar'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;